import {
  Button,
  ErrorState,
  Input,
  Label,
  PageHeader,
} from '@sitewise/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createBuilding,
  fetchBuilding,
  updateBuilding,
  type BuildingDetail,
} from '../../../api/buildings.js';
import { AddressAutocomplete } from '../../../components/address-autocomplete.js';
import { ApiError } from '../../../lib/api-client.js';

import type { PlaceAddressDetail } from '../../../api/places.js';

interface FormState {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  bin: string;
  bbl: string;
  notes: string;
}

const EMPTY: FormState = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: 'NY',
  postalCode: '',
  bin: '',
  bbl: '',
  notes: '',
};

function stateFromDetail(detail: BuildingDetail | undefined): FormState {
  if (!detail) return EMPTY;
  const b = detail.building;
  return {
    name: b.name,
    addressLine1: b.addressLine1,
    addressLine2: b.addressLine2 ?? '',
    city: b.city,
    state: b.state,
    postalCode: b.postalCode,
    bin: b.bin ?? '',
    bbl: b.bbl ?? '',
    notes: b.notes ?? '',
  };
}

export function BuildingFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = mode === 'edit';
  const buildingQuery = useQuery({
    queryKey: ['building', id],
    queryFn: () => fetchBuilding(id!),
    enabled: isEdit && Boolean(id),
  });
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && buildingQuery.data) setForm(stateFromDetail(buildingQuery.data));
  }, [isEdit, buildingQuery.data]);

  const mutation = useMutation({
    mutationFn: async (state: FormState) => {
      const payload = {
        name: state.name.trim(),
        addressLine1: state.addressLine1.trim(),
        addressLine2: state.addressLine2.trim() ? state.addressLine2.trim() : null,
        city: state.city.trim(),
        state: state.state.trim(),
        postalCode: state.postalCode.trim(),
        bin: state.bin.trim() ? state.bin.trim() : null,
        bbl: state.bbl.trim() ? state.bbl.trim() : null,
        notes: state.notes.trim() ? state.notes.trim() : null,
        unitCountOverride: null,
        metadata: {} as Record<string, string | number | boolean | null>,
      };
      if (isEdit) {
        return updateBuilding(id!, payload);
      }
      return createBuilding({ ...payload, contacts: [] });
    },
    onSuccess: (result) => {
      const newId = 'building' in result ? result.building.id : id;
      if (newId) navigate(`/buildings/${newId}`);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
        const details = err.details as { fieldErrors?: Record<string, string[]> } | null;
        if (details?.fieldErrors) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(details.fieldErrors)) {
            const message = Array.isArray(v) ? v[0] : (v as string);
            if (message) flat[k] = message;
          }
          setFieldErrors(flat);
        }
      } else {
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      }
    },
  });

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  }

  function applyGooglePlace(place: PlaceAddressDetail) {
    setForm((prev) => ({
      ...prev,
      addressLine1: place.addressLine1 || prev.addressLine1,
      // Only overwrite city/state/zip when Google actually returned them
      // and we either don't have a value yet or it doesn't match — keeps
      // partial overrides intact (e.g. user typed a custom locality).
      city: place.city || prev.city,
      state: place.state || prev.state,
      postalCode: place.postalCode || prev.postalCode,
    }));
    setFieldErrors({});
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    setFieldErrors({});
    mutation.mutate(form);
  }

  const title = useMemo(
    () => (isEdit ? `Edit ${buildingQuery.data?.building.name ?? 'building'}` : 'Add a building'),
    [isEdit, buildingQuery.data],
  );

  if (isEdit && buildingQuery.isError) {
    return <ErrorState description="Could not load this building." />;
  }

  return (
    <>
      <PageHeader
        title={title}
        description={
          isEdit
            ? 'Update address, BIN/BBL, or notes. Re-geocoding runs automatically when the address changes.'
            : 'A building is the unit of organization for everything else: units, contacts, work orders, compliance.'
        }
        breadcrumbs={[
          { label: 'Buildings', href: '/buildings' },
          { label: isEdit ? buildingQuery.data?.building.name ?? '…' : 'New' },
        ]}
        action={
          <Link to={isEdit && id ? `/buildings/${id}` : '/buildings'}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" /> Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={onSubmit} className="max-w-3xl space-y-6">
        <section className="space-y-4 rounded-lg border border-border bg-surface p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Building name" error={fieldErrors.name}>
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  required
                  autoFocus
                />
              )}
            </Field>
            <Field label="Notes" error={fieldErrors.notes}>
              {(id) => (
                <Input
                  id={id}
                  value={form.notes}
                  onChange={(e) => update('notes', e.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Street address" error={fieldErrors.addressLine1}>
            {(id) => (
              <AddressAutocomplete
                id={id}
                value={form.addressLine1}
                onChange={(v) => update('addressLine1', v)}
                onPlaceSelected={applyGooglePlace}
                required
                autoFocus={false}
              />
            )}
          </Field>

          <Field label="Address line 2" error={fieldErrors.addressLine2}>
            {(id) => (
              <Input
                id={id}
                value={form.addressLine2}
                onChange={(e) => update('addressLine2', e.target.value)}
                placeholder="Apt, suite, floor"
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="City" error={fieldErrors.city}>
              {(id) => (
                <Input
                  id={id}
                  value={form.city}
                  onChange={(e) => update('city', e.target.value)}
                  required
                />
              )}
            </Field>
            <Field label="State" error={fieldErrors.state}>
              {(id) => (
                <Input
                  id={id}
                  value={form.state}
                  onChange={(e) => update('state', e.target.value.toUpperCase())}
                  maxLength={2}
                  required
                />
              )}
            </Field>
            <Field label="ZIP" error={fieldErrors.postalCode}>
              {(id) => (
                <Input
                  id={id}
                  value={form.postalCode}
                  onChange={(e) => update('postalCode', e.target.value)}
                  required
                />
              )}
            </Field>
          </div>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-surface p-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold">Manual NYC identifiers</h2>
            <p className="text-sm text-fg-secondary">
              Provide both BIN and BBL to override the automatic NYC GeoSearch lookup. Leave blank
              to let SiteWise resolve them from the address.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="BIN" error={fieldErrors.bin} hint="7 digits">
              {(id) => (
                <Input
                  id={id}
                  value={form.bin}
                  onChange={(e) => update('bin', e.target.value)}
                  inputMode="numeric"
                  maxLength={7}
                />
              )}
            </Field>
            <Field label="BBL" error={fieldErrors.bbl} hint="10 digits">
              {(id) => (
                <Input
                  id={id}
                  value={form.bbl}
                  onChange={(e) => update('bbl', e.target.value)}
                  inputMode="numeric"
                  maxLength={10}
                />
              )}
            </Field>
          </div>
        </section>

        {errorMessage ? <ErrorState description={errorMessage} /> : null}

        <div className="flex items-center justify-end gap-2">
          <Link to={isEdit && id ? `/buildings/${id}` : '/buildings'}>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create building'}
          </Button>
        </div>
      </form>
    </>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  children: (id: string) => React.ReactNode;
}

function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children(id)}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {!error && hint ? <p className="text-xs text-fg-muted">{hint}</p> : null}
    </div>
  );
}
